import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { Realtime } from '../realtime/Realtime'
import type { RealtimeEvent } from '../realtime/events'
import type { TicketStatus } from '../domain/types'
import { hasPendingJourneyDebounce, hasPendingTicketDebounce } from '../lib/actionKeys'
import { ticketStatusFromEvent } from '../domain/ticketStatusFromEvent'

// Janela do coalescedor: uma rajada de N eventos do Pusher (uma ação local típica
// dispara 4-6: bilhete, agregador, sessão, fila) vira UM recarregamento em vez de N.
// Curta o suficiente para ser imperceptível, longa o suficiente para colapsar o burst.
export const REALTIME_COALESCE_MS = 150

export interface RealtimeSyncDeps {
  realtime: Realtime
  /** Chaves de ação com debounce local pendente (ver lib/actionKeys). */
  pendingDebounceKeys: () => Iterable<string>
  /** A rajada assentou: hora de recarregar. */
  onSync: () => void
  /** Presença mudou: só pede um re-render. */
  onPresenceChanged?: () => void
  /** Status do bilhete em memória, para comparar com o que o evento anuncia. */
  findTicket?: (ticketId: number) => { status: TicketStatus } | undefined
  /** Aplica o status otimista do evento (local está atrás do servidor). */
  onOptimisticStatus?: (ticketId: number, status: TicketStatus) => void

  coalesceMs?: number
}

/**
 * Liga a assinatura de tempo real e resume tudo o que o App precisa decidir a cada
 * evento: presença, supressão por debounce local, status otimista e coalescência.
 *
 * Os callbacks são lidos de um ref atualizado a cada render (latest-ref): a assinatura
 * é criada uma vez, mas nunca chama um callback velho.
 */
export function useRealtimeSync(deps: RealtimeSyncDeps): void {
  const depsRef = useRef(deps)
  useLayoutEffect(() => { depsRef.current = deps })

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { realtime } = deps

  const handleEvent = useCallback((e: RealtimeEvent) => {
    const current = depsRef.current

    if (e?.name === 'PresenceChanged') {
      current.onPresenceChanged?.()
      return
    }

    // 1. Entidade sob debounce local: por padrão o evento É eco do otimismo pendente
    // (o commit traz o estado final). EXCEÇÃO: evento de STATUS mapeável que diverge
    // do estado local — só outro operador consegue mudar o status durante nossa janela
    // de undo (ex.: TicketCalled concomitante ao meu cancel). Esse evento NÃO é engolido,
    // senão a UI diverge do servidor silenciosamente (audit ux-realtime #6).
    // Eventos sem status mapeável nessa entidade seguem suprimidos: não há como distinguir
    // eco de evento externo sem o status.
    const keys = Array.from(current.pendingDebounceKeys())
    let hasActiveDebounce = false
    if (e?.ticketId) {
      hasActiveDebounce = hasPendingTicketDebounce(keys, e.ticketId)
    }
    if (!hasActiveDebounce && e?.aggregatorId) {
      hasActiveDebounce = hasPendingJourneyDebounce(keys, e.aggregatorId)
    }

    // 2. Status que o evento anuncia: se o local já bate, o reload é redundante; se
    // está atrás, aplica otimista e ainda confirma com o servidor.
    let shouldReload = true
    let mappedStatus: TicketStatus | null = null
    if (e?.ticketId && e.name) {
      const mapped = ticketStatusFromEvent(e.name)
      if (mapped) {
        mappedStatus = mapped
        const local = current.findTicket?.(e.ticketId)
        if (local) {
          if (local.status === mapped) {
            shouldReload = false
          } else {
            current.onOptimisticStatus?.(e.ticketId, mapped)
          }
        }
      }
    }

    // Echo do otimismo pendente: status mapeado casou com o local (o eco do próprio
    // commit). Divergência sob debounce = evento externo — deixa passar.
    if (hasActiveDebounce && !mappedStatus) { return } // sem status mapeável: eco desconhecível
    if (!shouldReload) { return } // eco confirmado (ou estado local já casou)

    if (timeoutRef.current) { clearTimeout(timeoutRef.current) }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      void depsRef.current.onSync()
    }, current.coalesceMs ?? REALTIME_COALESCE_MS)
  }, [])

  useEffect(() => {
    realtime.start()
    const off = realtime.on(handleEvent)
    return () => {
      off()
      realtime.stop()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [realtime, handleEvent])
}
